import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { saveRequest } from '../ui/save-request';
import { useStaffSession, useScopePending, useCapability } from '../ui/StaffSession';
import { Permissions as P } from '@myfm/domain';
import { PageHeader } from '../ui/PageHeader';
import { ErrorState, Loading, Empty } from '../ui/State';
import { Link } from '../ui/ScopedLink';
import { ResponsiveTable } from '../ui/ResponsiveTable';
import { formatDateOnly, formatDateTime } from '../dates';
import { Action, Select, Text, downloadConveyancing, type Row, type Save } from './conveyancing-forms';
import { Choices } from './ConsultationForms';
export function BusinessInvoices() {
  const session = useStaffSession(),
    pending = useScopePending(),
    building = session?.effectiveScope.kind === 'building' ? session.effectiveScope : undefined,
    { invoiceId } = useParams(),
    [params] = useSearchParams();
  return building && !pending ? (
    <InvoicePage key={building.id + ':' + (invoiceId ?? '') + ':' + params.get('case')} building={building} id={invoiceId} caseId={params.get('case') ?? undefined} />
  ) : (
    <><PageHeader title="Business invoices" context="Review the managing agent’s direct business invoices for one building."/><Empty>Choose a building to view direct business invoices.</Empty></>
  );
}
function InvoicePage({ building, id, caseId }: { building: { id: string; name: string }; id?: string | undefined; caseId?: string | undefined }) {
  const [model, setModel] = useState<Row>(),
    [setup, setSetup] = useState<Row>(),
    [transaction, setTransaction] = useState<Row>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [page, setPage] = useState(1),
    [refresh, setRefresh] = useState(0);
  const manage = useCapability(P.ManageBusinessBilling),
    approve = useCapability(P.ApproveBusinessBilling);
  useEffect(() => {
    let active = true;
    setError(undefined);
    Promise.all([
      api<Row>(id ? '/api/business-invoices/' + id : `/api/business-invoices?building=${building.id}&page=${page}${caseId ? '&caseId=' + caseId : ''}`),
      api<Row>((caseId ? '/api/conveyancing/setup?building=' : '/api/business-invoices/options?building=') + building.id),
      caseId ? api<Row>('/api/conveyancing/' + caseId) : Promise.resolve(undefined)
    ])
      .then(([m, s, t]) => {
        if (active) {
          setModel(m);
          setSetup(s);
          setTransaction(t);
        }
      })
      .catch((e) => active && setError(e));
    return () => {
      active = false;
    };
  }, [id, building.id, page, refresh, caseId]);
  const save: Save = async (body, path) => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await saveRequest<Row>(path ?? '/api/business-invoices/' + id + '/commands', { method: 'POST', body: JSON.stringify(body) });
      setMessage('Saved. Business billing has been refreshed.');
      setRefresh((n) => n + 1);
      return result;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const download = async (path: string, name: string) => {
    try {
      await downloadConveyancing(path, name);
    } catch (e) {
      setError(e);
    }
  };
  if (!model || !setup) return error ? <ErrorState error={error} onRetry={() => setRefresh((n) => n + 1)} /> : <Loading />;
  const i = model.invoice,
    events = model.events ?? [],
    docs = setup.documents.filter((d: Row) => d.selectable);
  return (
    <div className="demand-workflow business-invoice-workspace">
      <PageHeader
        title={id ? (i.invoice_number ?? 'Draft business invoice') : 'Business invoices'}
        scope={{ kind: 'building', name: building.name }}
        context="Direct MYFM/BlockGenie billing to the named leaseholder. These charges and receipts do not enter service-charge accounts."
      />
      {Boolean(error) && <ErrorState error={error} />} {message && <p role="status">{message}</p>}
      <p>
        <Link to="/admin/conveyancing">Configure fees and business issuer</Link>
        {caseId && (
          <>
            {' '}
            · <Link to={'/contacts/conveyancing/' + caseId}>Back to conveyancing transaction</Link>
          </>
        )}
      </p>
      {!id ? (
        <>
          <ResponsiveTable>
            <caption>Direct business invoices</caption>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Bill to</th>
                <th>Payer</th>
                <th>Status</th>
                <th>Gross</th>
                <th>Outstanding</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {!model.items.length&&<tr><td colSpan={7}><Empty>No direct business invoices match this view.</Empty></td></tr>}
              {model.items.map((x: Row) => (
                <tr key={x.id}>
                  <td>
                    <Link to={'/finance/business-invoices/' + x.id}>{x.invoice_number ?? 'Draft invoice'}</Link>
                  </td>
                  <td>{x.debtor.name}</td>
                  <td>{x.payer_side}</td>
                  <td>{x.status}</td>
                  <td>£{x.gross}</td>
                  <td>£{x.position.outstanding}</td>
                  <td>{formatDateOnly(x.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
          <nav className="pagination" aria-label="Business invoice pages"><span>{model.total} invoices · Page {page} of {Math.max(1,Math.ceil(model.total/50))}</span>
          <button className="secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous invoices
          </button>
          <button className="secondary" disabled={page * 50 >= model.total} onClick={() => setPage((p) => p + 1)}>
            Next invoices
          </button></nav>
          {manage && caseId && transaction && <PrepareInvoice setup={setup} transaction={transaction} busy={busy} save={save} />}
          {!caseId && (
            <p>
              Prepare an invoice from its <Link to="/contacts/conveyancing">conveyancing transaction</Link>, retaining the property and debtor.
            </p>
          )}
        </>
      ) : (
        <>
          <p>
            <Link to="/finance/business-invoices">Back to invoices</Link> · <Link to={'/contacts/conveyancing/' + i.case_id}>Open transaction</Link>
          </p>
          <section>
            <h2>Invoice details</h2>
            <p>
              {i.debtor.name} — {i.debtor.address}
            </p>
            <p>
              {i.payer_side} · {i.status} · Supplied {formatDateOnly(i.supply_date)} · Due {formatDateOnly(i.due_date)}
            </p>
            <ResponsiveTable>
              <caption>Retained invoice prices</caption>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Quantity</th>
                  <th>Unit net</th>
                  <th>VAT rate</th>
                  <th>Net</th>
                  <th>VAT</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {i.lines.map((l: Row) => (
                  <tr key={l.serviceId}>
                    <td>{l.label}</td>
                    <td>{l.quantity}</td>
                    <td>£{l.unitNet}</td>
                    <td>{l.vatRate}%</td>
                    <td>£{l.net}</td>
                    <td>£{l.vat}</td>
                    <td>£{l.gross}</td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
            <p>
              Net £{i.net} · VAT £{i.vat} · Gross £{i.gross}
            </p>
            <p>
              Received £{model.position.paid} · Credited £{model.position.credited} · Outstanding £{model.position.outstanding}
            </p>
            <p>
              Issuer: {i.issuer.legalName} · {i.issuer.paymentInstructions}
            </p>
            <p>Charging authority: {i.authority.basis}</p>
            <button onClick={() => download('/api/business-invoices/' + id + '/pdf', (i.invoice_number ?? 'draft') + '.pdf')}>
              Download {i.status === 'Draft' ? 'draft' : 'issued'} invoice PDF
            </button>
          </section>
          {manage && i.status === 'Draft' && (
            <>
              {approve && (
                <Action
                  title="Approve and issue invoice"
                  busy={busy}
                  onSave={(v, key) => save({ action: 'Issue', version: i.version, documentIds: [], reason: v.reason, idempotencyKey: key })}
                >
                  <p>Issue the retained prices to {i.debtor.name}. The PDF is retained in Documents. This does not send it or post a service charge.</p>
                </Action>
              )}
              <Action
                title="Cancel unissued draft"
                busy={busy}
                onSave={(v, key) => save({ action: 'Cancel', version: i.version, documentIds: [], reason: v.reason, idempotencyKey: key })}
              />
            </>
          )}
          {manage && ['Issued', 'Credited'].includes(i.status) && (
            <>
              {['Payment', ...(approve ? ['Refund'] : [])].map((action) => (
                <Action
                  key={action}
                  title={action === 'Payment' ? 'Record received business payment' : 'Record completed business refund'}
                  busy={busy}
                  documents={docs}
                  onSave={(v, key, form) =>
                    save({
                      action,
                      version: i.version,
                      amount: v.amount,
                      paidOn: v.paidOn,
                      paymentSource: i.issuer.paymentSource,
                      reference: v.reference,
                      documentIds: form.getAll('documentIds'),
                      reason: v.reason,
                      idempotencyKey: key
                    })
                  }
                >
                  <p>Business payment source: {i.issuer.paymentSource}. This records evidence of an actual transaction; it does not move money.</p>
                  <Text name="amount" label={action === 'Payment' ? 'Payment amount (£)' : 'Refund amount (£)'} />
                  <Text name="paidOn" label="Actual bank transaction date" dateOnly />
                  <Text name="reference" label="Unique bank or payment transaction ID" />
                </Action>
              ))}
              {approve && (
                <>
                  <Action
                    title="Reverse an incorrect receipt allocation"
                    busy={busy}
                    documents={docs}
                    onSave={(v, key, form) =>
                      save({
                        action: 'Reverse payment',
                        version: i.version,
                        paymentId: v.paymentId,
                        documentIds: form.getAll('documentIds'),
                        reason: v.reason,
                        idempotencyKey: key
                      })
                    }
                  >
                    <Select
                      name="paymentId"
                      label="Unreversed receipt"
                      options={events
                        .filter((e: Row) => e.event_type === 'Payment' && !events.some((r: Row) => r.event_type === 'Payment reversed' && r.detail.paymentId === e.id))
                        .map((e: Row) => [e.id, `£${e.detail.amount} — ${e.detail.reference}`])}
                    />
                  </Action>
                  <Action
                    title="Issue a credit note"
                    busy={busy}
                    documents={docs}
                    onSave={(v, key, form) =>
                      save({ action: 'Credit', version: i.version, amount: v.amount, documentIds: form.getAll('documentIds'), reason: v.reason, idempotencyKey: key })
                    }
                  >
                    <Text name="amount" label="Gross credit amount (£)" />
                    <p>A credit preserves the invoice and records net/VAT allocation. Any refund is recorded separately after it happens.</p>
                  </Action>
                </>
              )}
              <Action
                title="Record invoice delivery"
                busy={busy}
                documents={docs}
                onSave={(v, key, form) =>
                  save({
                    action: 'Record delivery',
                    version: i.version,
                    recipient: v.recipient,
                    deliveredOn: v.deliveredOn,
                    method: v.method,
                    documentIds: form.getAll('documentIds'),
                    reason: v.reason,
                    idempotencyKey: key
                  })
                }
              >
                <Text name="recipient" label="Actual recipient" value={i.debtor.name} />
                <Text name="deliveredOn" label="Actual delivery date" dateOnly />
                <Select name="method" label="Delivery method" options={['Email', 'Post', 'Personal service', 'Other'].map((s) => [s, s])} />
              </Action>
            </>
          )}
          <section>
            <h2>Billing history</h2>
            {events.map((e: Row) => (
              <details key={e.id}>
                <summary>
                  {e.event_type} — {formatDateTime(e.created_at)}
                </summary>
                <p>{e.detail.reason}</p>
                {e.detail.amount && (
                  <p>
                    £{e.detail.amount} · {e.detail.reference}
                  </p>
                )}
                {e.detail.document && (
                  <p>
                    <Link to={'/documents/' + e.detail.document.id}>Open retained {e.event_type === 'Credit' ? 'credit' : 'invoice'}</Link> ·{' '}
                    <Link
                      to={'/communications/compose?document=' + e.detail.document.id + '&subject=' + encodeURIComponent(e.detail.number ?? i.invoice_number ?? 'Business invoice')}
                    >
                      Prepare email with document
                    </Link>
                  </p>
                )}
                {e.event_type === 'Credit' && (
                  <button onClick={() => download(`/api/business-invoices/${id}/pdf?creditId=${e.id}`, e.detail.number + '.pdf')}>Download credit note PDF</button>
                )}
              </details>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
function PrepareInvoice({ setup, transaction, busy, save }: { setup: Row; transaction: Row; busy: boolean; save: Save }) {
  const row = transaction.row,
    facts = row.transaction_details,
    [side, setSide] = useState(''),
    [debtor, setDebtor] = useState(''),
    [scheduleId, setScheduleId] = useState(setup.schedules.find((s: Row) => s.effective_from.slice(0, 10) <= new Date().toISOString().slice(0, 10))?.id ?? ''),
    schedule = setup.schedules.find((s: Row) => s.id === scheduleId),
    outgoing = setup.interests.find((i: Row) => i.id === facts.outgoingInterestId)?.parties ?? [];
  const allowed = (person: string | null, org: string | null) =>
    side === 'Incoming leaseholder'
      ? person
        ? facts.incomingPersonIds.includes(person)
        : facts.incomingOrganisationId === org
      : outgoing.some((p: Row) => (person ? p.personId === person : p.organisationId === org));
  const people = setup.people.filter((p: Row) => allowed(p.id, null)).map((p: Row) => ['person:' + p.id, p.display_name]),
    orgs = setup.organisations.filter((o: Row) => allowed(null, o.id)).map((o: Row) => ['organisation:' + o.id, o.legal_name]),
    [partyType, partyId] = debtor.split(':'),
    addresses = setup.addresses.filter((a: Row) => (partyType === 'person' ? a.person_id === partyId : a.organisation_id === partyId));
  return (
    <Action
      title="Prepare direct business invoice"
      busy={busy}
      documents={setup.documents.filter((d: Row) => d.selectable)}
      onSave={(v, key, form) => {
        const [kind, id] = String(v.debtor).split(':'),
          summary = setup.documents.find((d: Row) => d.id === v.rightsSummaryDocumentId);
        return save(
          {
            payerSide: v.payerSide,
            debtor: { personId: kind === 'person' ? id : null, organisationId: kind === 'organisation' ? id : null, addressAssignmentId: v.addressAssignmentId },
            profileId: v.profileId,
            scheduleId: v.scheduleId,
            lines: form.getAll('serviceIds').map((serviceId) => ({ serviceId, code: form.get('code:' + serviceId), quantity: Number(form.get('quantity:' + serviceId)) })),
            supplyDate: v.supplyDate,
            dueDate: v.dueDate,
            authority: {
              basis: v.basis,
              classification: v.classification,
              jurisdiction: facts.jurisdiction,
              rightsSummaryDocumentId: summary?.id ?? null,
              rightsSummaryVersionId: summary?.version_id ?? null
            },
            documentIds: form.getAll('documentIds'),
            reason: v.reason,
            idempotencyKey: key
          },
          '/api/conveyancing/' + row.id + '/invoices'
        );
      }}
    >
      <Select
        name="payerSide"
        label="Who is being invoiced?"
        options={['Outgoing leaseholder', 'Incoming leaseholder'].map((s) => [s, s])}
        onChange={(v) => {
          setSide(v);
          setDebtor('');
        }}
      />
      <Select key={side} name="debtor" label="Named leaseholder debtor" options={[...people, ...orgs]} value={debtor} onChange={setDebtor} />
      <Select key={debtor} name="addressAssignmentId" label="Debtor billing address" options={addresses.map((a: Row) => [a.id, a.address])} />
      <Select
        name="profileId"
        label="Approved business issuer"
        options={setup.profiles.filter((p: Row) => p.approved_at).map((p: Row) => [p.id, p.details.legalName + ' — ' + formatDateOnly(p.created_at)])}
      />
      <Select
        name="scheduleId"
        label="Fee schedule"
        value={scheduleId}
        options={setup.schedules.map((s: Row) => [s.id, s.title + ' — ' + formatDateOnly(s.effective_from)])}
        onChange={setScheduleId}
      />
      <fieldset>
        <legend>Services to invoice</legend>
        {transaction.services.map((s: Row) => (
          <fieldset key={s.id}>
            <legend>
              <label>
                <input type="checkbox" name="serviceIds" value={s.id} />
                {s.title}
              </label>
            </legend>
            <Select
              name={'code:' + s.id}
              label={'Price for ' + s.title}
              optional
              options={(schedule?.items ?? []).map((f: Row) => [f.code, `${f.label} — £${f.net} net + ${f.vatRate}% VAT`])}
            />
            <Text name={'quantity:' + s.id} label={'Quantity for ' + s.title} type="number" value="1" />
          </fieldset>
        ))}
      </fieldset>
      <Text name="supplyDate" label="Service supply date" dateOnly />
      <Text name="dueDate" label="Payment due date" dateOnly />
      <Select name="classification" label="Charging basis" options={['Agreed direct service', 'Lease administration charge'].map((s) => [s, s])} />
      <Text name="basis" label="Reviewed fee agreement or lease authority" type="textarea" />
      <Select
        name="rightsSummaryDocumentId"
        label="Reviewed administration-charge rights summary, where required"
        optional
        options={setup.documents.filter((d: Row) => d.selectable).map((d: Row) => [d.id, d.title])}
      />
      <p>Check the agreed payer and included services. Combined Transfer and Charge cannot be billed alongside either included notice.</p>
    </Action>
  );
}
